import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, Truck, RotateCcw, Info, Tag, CalendarDays, AlertCircle } from 'lucide-react';
import { BOOKING_POLICY } from '../../config/pricingRules';

/**
 * BookingInfoBlock — Bloc d'informations tarifaires et conditions de location
 * Utilisé sur : ProductPage, PanierPage, DevisPage, MonComptePage
 *
 * Props:
 *   compact   : bool — version condensée (accordéon)
 *   showTitle : bool — afficher le titre
 *   dark      : bool — thème sombre (admin)
 */
export default function BookingInfoBlock({ compact = false, showTitle = true, dark = false }) {
  const [open, setOpen] = useState(!compact);

  const ink    = dark ? '#fff'                        : 'var(--ink)';
  const muted  = dark ? 'rgba(255,255,255,0.45)'      : 'var(--text-muted)';
  const bg     = dark ? '#161616'                     : '#FFFFFF';
  const border = dark ? 'rgba(255,255,255,0.08)'      : 'var(--border)';
  const teal   = dark ? '#5DBCBF'                     : 'var(--teal)';
  const tealBg = dark ? 'rgba(93,188,191,0.08)'       : 'var(--teal-mist)';
  const gold   = dark ? '#D4AF37'                     : '#C58B19';
  const goldBg = dark ? 'rgba(212,175,55,0.08)'       : 'rgba(197,139,25,0.06)';
  const green  = '#1F9D7A';
  const greenBg = 'rgba(31,157,122,0.06)';

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${border}`, background: bg }}>
      {/* En-tête (accordéon si compact) */}
      <button
        onClick={() => compact && setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
        style={{
          background: tealBg,
          borderBottom: open ? `1px solid ${border}` : 'none',
          cursor: compact ? 'pointer' : 'default',
          border: 'none',
          textAlign: 'left',
        }}>
        <div className="flex items-center gap-2">
          <Info size={15} style={{ color: teal, flexShrink: 0 }} />
          {showTitle && (
            <p className="text-sm font-bold" style={{ color: teal }}>
              Informations & conditions de location
            </p>
          )}
        </div>
        {compact && (open ? <ChevronUp size={15} style={{ color: teal }} /> : <ChevronDown size={15} style={{ color: teal }} />)}
      </button>

      {open && (
        <div className="p-4 space-y-4">

          {/* ─ Tarifs ─ */}
          <div className="flex items-start gap-2.5 p-3 rounded-xl"
            style={{ background: tealBg, border: `1px solid ${teal}20` }}>
            <Tag size={13} style={{ color: teal, flexShrink: 0, marginTop: 1 }} />
            <p className="text-xs font-semibold" style={{ color: dark ? teal : '#0B4B4E' }}>
              {BOOKING_POLICY.tarifs}
            </p>
          </div>

          {/* ─ Options de réservation ─ */}
          <div>
            <p className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: muted }}>Options de réservation</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: '🌅', label: 'Demi-journée matin',      sub: '−25% en semaine · jusqu\'à 12h' },
                { icon: '☀️', label: 'Demi-journée après-midi', sub: '−25% en semaine · dès 13h' },
                { icon: '📅', label: 'Journée complète',         sub: 'Tarif de base' },
                { icon: '🗓️', label: 'Plusieurs jours',           sub: '-15% dès le 3ème jour' },
              ].map((opt, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-xl"
                  style={{ background: dark ? 'rgba(255,255,255,0.04)' : 'var(--bg)', border: `1px solid ${border}` }}>
                  <span className="text-base">{opt.icon}</span>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: ink }}>{opt.label}</p>
                    <p className="text-xs" style={{ color: muted }}>{opt.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs mt-2" style={{ color: muted }}>
              Demi-journée : −25% du lundi au vendredi · plein tarif le week-end (samedi/dimanche).
            </p>
          </div>

          {/* ─ Horaires de livraison ─ */}
          <div>
            <p className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: muted }}>Horaires de livraison</p>
            <div className="space-y-1.5">
              {[
                { type: 'Journée complète',          horaire: BOOKING_POLICY.horaires.journee },
                { type: 'Demi-journée matin',        horaire: BOOKING_POLICY.horaires.demi_matin },
                { type: 'Demi-journée après-midi',   horaire: BOOKING_POLICY.horaires.demi_aprem },
              ].map((h, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg"
                  style={{ background: dark ? 'rgba(255,255,255,0.04)' : 'var(--bg)', border: `1px solid ${border}` }}>
                  <span className="text-xs" style={{ color: muted }}>{h.type}</span>
                  <span className="text-xs font-bold" style={{ color: teal }}>
                    <Truck size={10} className="inline mr-1" />{h.horaire}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs mt-2 flex items-start gap-1.5" style={{ color: muted }}>
              <Clock size={11} style={{ flexShrink: 0, marginTop: 1 }} />
              {BOOKING_POLICY.installation}
            </p>
          </div>

          {/* ─ Réductions ─ */}
          <div>
            <p className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: muted }}>Réductions automatiques</p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 p-3 rounded-xl"
                style={{ background: greenBg, border: `1px solid ${green}25` }}>
                <span className="text-base">📅</span>
                <div>
                  <p className="text-xs font-bold" style={{ color: green }}>Réduction semaine — Lundi au Jeudi : -15%</p>
                  <p className="text-xs" style={{ color: muted }}>{BOOKING_POLICY.reductionSem}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-xl"
                style={{ background: greenBg, border: `1px solid ${green}25` }}>
                <span className="text-base">🗓️</span>
                <div>
                  <p className="text-xs font-bold" style={{ color: green }}>Multi-jours : -15% dès le 3ème jour</p>
                  <p className="text-xs" style={{ color: muted }}>1j: tarif normal · 2j: ×2 · 3j et +: -15% sur le 3ème jour et suivants</p>
                </div>
              </div>
            </div>
          </div>

          {/* ─ Options supplémentaires ─ */}
          <div>
            <p className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: muted }}>Options disponibles</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-start gap-2 p-2.5 rounded-xl"
                style={{ background: goldBg, border: `1px solid ${gold}25` }}>
                <Truck size={12} style={{ color: gold, flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-xs font-bold" style={{ color: gold }}>Livraison la veille</p>
                  <p className="text-xs font-bold" style={{ color: gold }}>+60€</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-2.5 rounded-xl"
                style={{ background: goldBg, border: `1px solid ${gold}25` }}>
                <RotateCcw size={12} style={{ color: gold, flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-xs font-bold" style={{ color: gold }}>Récup. lendemain matin</p>
                  <p className="text-xs font-bold" style={{ color: gold }}>+60€</p>
                </div>
              </div>
            </div>
          </div>

          {/* ─ Informations importantes ─ */}
          <div className="space-y-2">
            <div className="flex items-start gap-2 p-2.5 rounded-xl"
              style={{ background: goldBg, border: `1px solid ${gold}20` }}>
              <AlertCircle size={12} style={{ color: gold, flexShrink: 0, marginTop: 1 }} />
              <p className="text-xs" style={{ color: gold }}>
                <strong>Retour matériel :</strong> {BOOKING_POLICY.retour}
              </p>
            </div>
            <div className="flex items-start gap-2 p-2.5 rounded-xl"
              style={{ background: goldBg, border: `1px solid ${gold}20` }}>
              <Clock size={12} style={{ color: gold, flexShrink: 0, marginTop: 1 }} />
              <p className="text-xs" style={{ color: gold }}>
                <strong>Récupérations</strong> effectuées jusqu'à 19h maximum.
                {' '}{BOOKING_POLICY.anticipation}
              </p>
            </div>
          </div>

          {/* ─ Champs requis à la réservation ─ */}
          <div className="p-3 rounded-xl" style={{ background: dark ? 'rgba(255,255,255,0.03)' : 'var(--bg)', border: `1px solid ${border}` }}>
            <p className="text-xs font-bold mb-2" style={{ color: ink }}>Merci d'indiquer lors de votre réservation :</p>
            <ul className="space-y-1">
              {[
                'Heure de début de l\'événement',
                'Heure de fin de l\'événement',
                'Type de réservation souhaité',
                'Besoins de livraison et récupération',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-1.5 text-xs" style={{ color: muted }}>
                  <span style={{ color: teal }}>•</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
